import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatCurrency } from '@/lib/types/job';
import { BidLineItem } from '@/hooks/useBidLineItems';
import { Calculator } from 'lucide-react';

interface BidLineItemsViewProps {
  lineItems: BidLineItem[];
  specifications?: Array<{ id: string; name: string }>;
  quantityOptions?: Array<{ id: string; specification_id: string; quantity_option: number; unit: string }>;
  compact?: boolean;
  hideGrandTotal?: boolean;
  showFulfillmentLocation?: boolean;
  totalVendorTaxCents?: number;
}

export const BidLineItemsView = ({ 
  lineItems, 
  specifications, 
  quantityOptions, 
  compact = false, 
  hideGrandTotal = false, 
  showFulfillmentLocation = false,
  totalVendorTaxCents = 0
}: BidLineItemsViewProps) => {
  // Group line items by specification
  const itemsBySpec = lineItems.reduce((acc, item) => {
    const specId = item.specification_id;
    if (!acc[specId]) {
      acc[specId] = [];
    }
    acc[specId].push(item);
    return acc;
  }, {} as Record<string, BidLineItem[]>);

  const calculateSubtotal = () => {
    return lineItems.reduce((sum, item) => sum + item.total_price_cents, 0);
  };

  const calculateTotal = () => {
    return calculateSubtotal() + totalVendorTaxCents;
  };

  const getSpecName = (specId: string) => {
    return specifications?.find(s => s.id === specId)?.name || 'Unknown Specification';
  };

  const getLocationDisplay = (item: BidLineItem) => {
    const loc = item.origin_location;
    if (!loc) return null;
    if (loc.location_name && loc.city && loc.state) {
      return `${loc.location_name} (${loc.city}, ${loc.state})`;
    }
    if (loc.city && loc.state) {
      return `${loc.city}, ${loc.state}`;
    }
    return loc.location_name || null;
  };

  if (compact) {
    return (
      <div className="space-y-2">
        <div className="text-sm font-medium">
          Subtotal: {formatCurrency(calculateSubtotal())}
        </div>
        {totalVendorTaxCents > 0 && (
          <div className="text-sm text-muted-foreground flex items-center gap-1">
            <Calculator className="h-3 w-3" />
            Est. Tax: {formatCurrency(totalVendorTaxCents)}
          </div>
        )}
        {totalVendorTaxCents > 0 && (
          <div className="text-sm font-semibold">
            Total: {formatCurrency(calculateTotal())}
          </div>
        )}
        <div className="text-xs text-muted-foreground">
          {lineItems.length} line item{lineItems.length !== 1 ? 's' : ''}
        </div>
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Line Items Breakdown</CardTitle>
          <Badge variant="outline">{lineItems.length} items</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {Object.entries(itemsBySpec).map(([specId, items]) => {
          return (
            <div key={specId} className="border rounded-lg p-4">
              <div className="mb-4">
                <div className="font-medium text-base">{getSpecName(specId)}</div>
                <div className="text-xs text-muted-foreground">
                  {items.length} quantity option{items.length !== 1 ? 's' : ''}
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Quantity</TableHead>
                    <TableHead className="text-right">Unit Price</TableHead>
                    <TableHead className="text-right">Total Price</TableHead>
                    <TableHead className="text-right">Est. Tax</TableHead>
                    {showFulfillmentLocation && <TableHead>Fulfillment Location</TableHead>}
                    <TableHead className="text-right">Lead Time</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((item) => {
                    const qtyOption = quantityOptions?.find(qo => 
                      qo.specification_id === item.specification_id && 
                      qo.quantity_option === item.quantity_option
                    );
                    
                    return (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div className="font-medium">
                            {qtyOption ? `${qtyOption.quantity_option.toLocaleString()} ${qtyOption.unit}` : item.quantity_option.toLocaleString()}
                          </div>
                          {item.notes && (
                            <div className="text-xs text-muted-foreground">{item.notes}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(item.unit_price_cents)}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.total_price_cents)}
                        </TableCell>
                        <TableCell className="text-right text-muted-foreground">
                          {item.tax_cents && item.tax_cents > 0 
                            ? formatCurrency(item.tax_cents)
                            : <span className="text-xs">—</span>
                          }
                        </TableCell>
                        {showFulfillmentLocation && (
                          <TableCell>
                            {getLocationDisplay(item) || (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                        )}
                        <TableCell className="text-right">
                          {item.lead_time_days} days
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          );
        })}

        {!hideGrandTotal && (
          <div className="flex justify-end pt-4 border-t">
            <div className="text-right space-y-1">
              <div>
                <span className="text-sm text-muted-foreground">Subtotal: </span>
                <span className="font-medium">{formatCurrency(calculateSubtotal())}</span>
              </div>
              {totalVendorTaxCents > 0 && (
                <div className="flex items-center justify-end gap-1">
                  <Calculator className="h-3 w-3 text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">Est. Tax: </span>
                  <span className="font-medium">{formatCurrency(totalVendorTaxCents)}</span>
                </div>
              )}
              <div className="pt-1">
                <span className="text-sm text-muted-foreground">Total: </span>
                <span className="text-2xl font-bold">{formatCurrency(calculateTotal())}</span>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
